import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material'

import { FC, MouseEvent, useMemo } from 'react'

import { PreviewTrailSystem } from '../../models/PreviewTrailSystem.model'
import { TrailSystemStatus } from './TrailSystemStatus.component'

// Override the global MuiPaper gradient so preview cards match the flat, rounded surface used in the public app.
const StyledCard = styled(Card)(({ theme }) => ({
  background: theme.palette.grey[800],
  borderRadius: 12,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
}))

type TrailSystemCardProps = {
  trailSystem: PreviewTrailSystem
  anyFreezeThawActive?: boolean
  onSelect?: (systemId: number) => void
}

export const TrailSystemCard: FC<TrailSystemCardProps> = (props) => {
  const trailStats = useMemo(() => {
    const initialStats = { closed: 0, count: 0, highImpact: 0, lowImpact: 0, mediumImpact: 0, miles: 0 }
    return (props.trailSystem.trails || []).reduce(
      (acc, trail) => ({
        closed: acc.closed + (trail.status?.status === 0 ? 1 : 0),
        count: acc.count + 1,
        highImpact: acc.highImpact + (trail.status?.status === 1 ? 1 : 0),
        lowImpact: acc.lowImpact + (trail.status?.status === 3 ? 1 : 0),
        mediumImpact: acc.mediumImpact + (trail.status?.status === 2 ? 1 : 0),
        miles: Math.round((acc.miles + (trail.distance || 0)) * 10) / 10,
      }),
      initialStats,
    )
  }, [props.trailSystem.trails])

  const trailFreezeThawStats = useMemo(() => {
    const initialStats = { closed: 0, count: 0, highImpact: 0, lowImpact: 0, mediumImpact: 0 }
    return (props.trailSystem.trails || []).reduce((acc, trail) => {
      const isFreezeThaw = !!trail.status?.freezeThawStatus?.status && trail.status.freezeThawStatus.status > 0
      return {
        closed: acc.closed + (isFreezeThaw && trail.status?.status === 0 ? 1 : 0),
        count: acc.count + (isFreezeThaw ? 1 : 0),
        highImpact: acc.highImpact + (isFreezeThaw && trail.status?.status === 1 ? 1 : 0),
        lowImpact: acc.lowImpact + (isFreezeThaw && trail.status?.status === 3 ? 1 : 0),
        mediumImpact: acc.mediumImpact + (isFreezeThaw && trail.status?.status === 2 ? 1 : 0),
      }
    }, initialStats)
  }, [props.trailSystem.trails])

  const handleEditClick = (event: MouseEvent) => {
    // The edit button overlays the drill-in action area, so stop propagation to keep the click local to edit.
    event.stopPropagation()
  }

  return (
    <StyledCard>
      <CardActionArea onClick={() => props.onSelect?.(props.trailSystem.id)} sx={{ flexGrow: 1 }}>
        <CardContent>
          <Stack alignItems="center" spacing={2}>
            <Stack alignItems="stretch" width="100%">
              <Stack>
                <Typography component="h2" variant="h5" fontWeight={700} fontSize={24}>
                  {props.trailSystem.name}
                </Typography>
                <Typography component="h3" variant="subtitle2" fontSize={14}>
                  {trailStats.miles} miles of trails
                </Typography>
              </Stack>
            </Stack>
            <TrailSystemStatus
              values={{
                closed: trailStats.closed,
                highImpact: trailStats.highImpact - trailFreezeThawStats.highImpact,
                lowImpact: trailStats.lowImpact - trailFreezeThawStats.lowImpact,
                mediumImpact: trailStats.mediumImpact - trailFreezeThawStats.mediumImpact,
                freezeThaw: trailFreezeThawStats.count,
              }}
              count={trailStats.count}
              showFreezeThawRing={props.anyFreezeThawActive}
            />
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: 'center', pb: 1.5, pt: 0 }}>
          <Box
            position="relative"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight={24}
          >
            {!props.trailSystem.active && <Chip label="Inactive" size="small" sx={{ left: 0, position: 'absolute' }} />}
            <AddIcon fontSize="medium" />
            <Tooltip title="Edit trail system">
              <IconButton
                aria-label="Edit trail system"
                href={`/trail-systems/${props.trailSystem.id}`}
                onClick={handleEditClick}
                size="small"
                sx={(theme) => ({ color: theme.palette.grey[400], position: 'absolute', right: 0 })}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </CardActions>
      </CardActionArea>
    </StyledCard>
  )
}
TrailSystemCard.displayName = 'TrailSystemCard'
